import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { usersService } from '@/services/users.service';
import { processesService } from '@/services/processes.service';
import {
  ArrowLeft,
  Search,
  Plus,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  ListTodo,
  UserCog,
} from 'lucide-react';

export function EmployeeProcesses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  // Buscar funcionário
  const { data: user } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersService.getById(id!),
    enabled: !!id,
  });

  // Buscar processos associados
  const { data: userProcesses, isLoading: loadingUserProcesses } = useQuery({
    queryKey: ['user-processes', id],
    queryFn: () => usersService.getUserProcesses(id!),
    enabled: !!id,
  });

  // Buscar todos os processos disponíveis
  const { data: allProcesses, isLoading: loadingAllProcesses } = useQuery({
    queryKey: ['processes'],
    queryFn: () => processesService.getAll(),
  });

  const addProcessMutation = useMutation({
    mutationFn: ({ userId, processId }: { userId: string; processId: string }) =>
      usersService.addProcess(userId, processId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-processes', id] });
      toast({
        title: 'Processo associado!',
        description: 'O processo foi associado ao funcionário com sucesso.',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro!',
        description: error.response?.data?.message || 'Não foi possível associar o processo.',
        variant: 'destructive',
      });
    },
  });

  const removeProcessMutation = useMutation({
    mutationFn: ({ userId, processId }: { userId: string; processId: string }) =>
      usersService.removeProcess(userId, processId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-processes', id] });
      toast({
        title: 'Processo removido!',
        description: 'O processo foi desassociado do funcionário com sucesso.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro!',
        description: 'Não foi possível remover o processo.',
        variant: 'destructive',
      });
    },
  });

  const handleAddProcess = (processId: string) => {
    if (id) {
      addProcessMutation.mutate({ userId: id, processId });
    }
  };

  const handleRemoveProcess = (processId: string, processName: string) => {
    if (!confirm(`Tem certeza que deseja remover o processo "${processName}"?`)) {
      return;
    }
    if (id) {
      removeProcessMutation.mutate({ userId: id, processId });
    }
  };

  // Filtrar processos disponíveis (que ainda não estão associados)
  const userProcessIds = new Set(userProcesses?.map((p) => p.id) || []);
  const availableProcesses = allProcesses?.filter(
    (p) => !userProcessIds.has(p.id) && p.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredUserProcesses = userProcesses?.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const frequencyLabels: Record<string, string> = {
    DAILY: 'Diário',
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensal',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/app/funcionarios')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-brand-gold">Processos do Funcionário</h1>
            <p className="text-brand-ivory/60 mt-1">
              {user?.name && <span>{user.name} - </span>}
              Gerencie os processos associados ao funcionário
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar processos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
        </div>
      </Card>

      {/* Processos Associados */}
      <div>
        <h2 className="text-xl font-serif text-brand-gold mb-4">Processos Associados</h2>
        {loadingUserProcesses ? (
          <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>
        ) : filteredUserProcesses && filteredUserProcesses.length > 0 ? (
          <div className="grid gap-4">
            {filteredUserProcesses.map((process) => (
              <motion.div
                key={process.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20">
                  <div className="p-6 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="w-5 h-5 text-brand-gold" />
                        <h3 className="text-lg font-serif text-brand-gold">{process.name}</h3>
                      </div>
                      {process.description && (
                        <p className="text-sm text-brand-ivory/60 mb-3">{process.description}</p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                          {frequencyLabels[process.frequency] || process.frequency}
                        </span>
                        {process.active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                            <CheckCircle className="h-3 w-3" />
                            Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                            <XCircle className="h-3 w-3" />
                            Inativo
                          </span>
                        )}
                        <span className="text-xs text-brand-ivory/50">
                          {process.tasks?.length || 0} tarefas
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveProcess(process.id, process.name)}
                      disabled={removeProcessMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-8 text-center text-brand-ivory/50">
              <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum processo associado ainda</p>
            </div>
          </Card>
        )}
      </div>

      {/* Processos Disponíveis */}
      <div>
        <h2 className="text-xl font-serif text-brand-gold mb-4">Processos Disponíveis</h2>
        {loadingAllProcesses ? (
          <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>
        ) : availableProcesses && availableProcesses.length > 0 ? (
          <div className="grid gap-4">
            {availableProcesses.map((process) => (
              <motion.div
                key={process.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                  <div className="p-6 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="w-5 h-5 text-brand-gold/60" />
                        <h3 className="text-lg font-serif text-brand-gold">{process.name}</h3>
                      </div>
                      {process.description && (
                        <p className="text-sm text-brand-ivory/60 mb-3">{process.description}</p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                          {frequencyLabels[process.frequency] || process.frequency}
                        </span>
                        <span className="text-xs text-brand-ivory/50">
                          {process.tasks?.length || 0} tarefas
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleAddProcess(process.id)}
                      disabled={addProcessMutation.isPending}
                      variant="outline"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Associar
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-8 text-center text-brand-ivory/50">
              <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>
                {search
                  ? 'Nenhum processo disponível encontrado'
                  : 'Todos os processos já foram associados'}
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
